/**
 * Intake Service
 *
 * Handles persistence of IntakeProfile data to Firestore.
 * Stores the profile at users/{userId}/intakeProfile.
 */

import type { IntakeProfile } from "../types/intake";
import type { Result } from "../types/result";
import type { AppError } from "../types/errors";
import { createUnexpectedError } from "../types/errors";
import { getFirestoreClient } from "../lib/firestoreClient";

/**
 * Save the intake profile to Firestore.
 * Writes are awaited to ensure acknowledgment.
 */
export async function saveIntakeProfile(
  userId: string,
  profile: IntakeProfile
): Promise<Result<void, AppError>> {
  try {
    const path = ["users", userId, "intake", "profile"];
    const client = getFirestoreClient();

    await client.setDocument(
      path,
      {
        ...profile,
        completedAt: client.serverTimestamp(),
      } as Record<string, unknown>,
      { merge: true }
    );

    return { ok: true, value: undefined };
  } catch (error) {
    return {
      ok: false,
      error: createUnexpectedError({
        error,
        context: `Saving intake profile for userId=${userId}`,
      }),
    };
  }
}

/**
 * Load the intake profile from Firestore.
 * Returns null if no profile exists yet for the given user.
 */
export async function getIntakeProfile(
  userId: string
): Promise<Result<IntakeProfile | null, AppError>> {
  try {
    const client = getFirestoreClient();
    const path = ["users", userId, "intake", "profile"];
    const snap = await client.getDocument(path);

    if (!snap.exists()) {
      return { ok: true, value: null };
    }

    return { ok: true, value: snap.data() as unknown as IntakeProfile };
  } catch (error) {
    return {
      ok: false,
      error: createUnexpectedError({
        error,
        context: `Loading intake profile for userId=${userId}`,
      }),
    };
  }
}
